import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Avatar,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Search,
  Edit,
  Delete,
  Block,
  CheckCircle,
  Person,
  Email,
  Phone,
  LocationOn,
  CalendarToday,
  Store,
  VerifiedUser,
  HourglassEmpty,
  Cancel,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { getUsers, updateUser, deleteUser, clearError } from '../store/slices/userSlice';

const Users = () => {
  const dispatch = useDispatch();
  const { users, isLoading, error } = useSelector((state) => state.users);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [filteredUsers, setFilteredUsers] = useState([]);

  useEffect(() => {
    dispatch(getUsers());
  }, [dispatch]);

  useEffect(() => {
    if (users && Array.isArray(users)) {
      const filtered = users.filter((user) =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers([]);
    }
  }, [searchTerm, users]);

  const handleEdit = (user) => {
    setSelectedUser(user);
    setOpenDialog(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      dispatch(deleteUser(id));
    }
  };

  const handleToggleStatus = (user) => {
    dispatch(updateUser({
      id: user._id,
      userData: { isActive: !user.isActive }
    }));
  };

  // Handle seller verification status change
  const handleVerifySeller = (user, status) => {
    dispatch(updateUser({
      id: user._id,
      userData: { sellerStatus: status }
    }));
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
  };

  const stats = {
    total: users?.length || 0,
    active: users?.filter(u => u.isActive !== false).length || 0,
    inactive: users?.filter(u => u.isActive === false).length || 0,
    newThisMonth: users?.filter(u => {
      if (!u.createdAt) return false;
      const created = new Date(u.createdAt);
      const now = new Date();
      return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
    }).length || 0,
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          color: '#000000',
          fontWeight: 700,
          pb: 2,
          mb: 3,
          borderBottom: '2px solid #ff2852',
        }}
      >
        Users Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearError())}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 16px rgba(123, 44, 191, 0.2)',
              borderColor: '#ff2852',
              transform: 'translateY(-2px)',
            },
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography sx={{ color: '#666666', fontWeight: 500, fontSize: '0.875rem', mb: 1 }}>
                    Total Users
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#000000', fontWeight: 700 }}>
                    {stats.total}
                  </Typography>
                </Box>
                <Box sx={{
                  backgroundColor: '#ff2852',
                  borderRadius: '50%',
                  width: 60,
                  height: 60,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  boxShadow: '0 2px 8px rgba(123, 44, 191, 0.3)',
                }}>
                  <Person />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 16px rgba(123, 44, 191, 0.2)',
              borderColor: '#ff2852',
              transform: 'translateY(-2px)',
            },
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography sx={{ color: '#666666', fontWeight: 500, fontSize: '0.875rem', mb: 1 }}>
                    Active Users
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#000000', fontWeight: 700 }}>
                    {stats.active}
                  </Typography>
                </Box>
                <Box sx={{
                  backgroundColor: '#ff2852',
                  borderRadius: '50%',
                  width: 60,
                  height: 60,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  boxShadow: '0 2px 8px rgba(123, 44, 191, 0.3)',
                }}>
                  <CheckCircle />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 16px rgba(123, 44, 191, 0.2)',
              borderColor: '#ff2852',
              transform: 'translateY(-2px)',
            },
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography sx={{ color: '#666666', fontWeight: 500, fontSize: '0.875rem', mb: 1 }}>
                    Inactive Users
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#000000', fontWeight: 700 }}>
                    {stats.inactive}
                  </Typography>
                </Box>
                <Box sx={{
                  backgroundColor: '#ff2852',
                  borderRadius: '50%',
                  width: 60,
                  height: 60,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  boxShadow: '0 2px 8px rgba(123, 44, 191, 0.3)',
                }}>
                  <Block />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 16px rgba(123, 44, 191, 0.2)',
              borderColor: '#ff2852',
              transform: 'translateY(-2px)',
            },
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography sx={{ color: '#666666', fontWeight: 500, fontSize: '0.875rem', mb: 1 }}>
                    New This Month
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#000000', fontWeight: 700 }}>
                    {stats.newThisMonth}
                  </Typography>
                </Box>
                <Box sx={{
                  backgroundColor: '#ff2852',
                  borderRadius: '50%',
                  width: 60,
                  height: 60,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  boxShadow: '0 2px 8px rgba(123, 44, 191, 0.3)',
                }}>
                  <CalendarToday />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search Bar */}
      <Paper sx={{
        p: 2,
        mb: 3,
        backgroundColor: '#ffffff',
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      }}>
        <TextField
          fullWidth
          placeholder="Search users by name, email, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#ff2852' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#e0e0e0',
              },
              '&:hover fieldset': {
                borderColor: '#ff2852',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#ff2852',
              },
            },
          }}
        />
      </Paper>

      {/* Users Table */}
      <Paper sx={{
        backgroundColor: '#ffffff',
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      }}>
        {!filteredUsers || filteredUsers.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Person sx={{ fontSize: 64, color: '#9e9e9e', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#666666' }} gutterBottom>
              {searchTerm ? 'No users found' : 'No Users Found'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666666' }}>
              {searchTerm ? 'Try adjusting your search terms' : 'Users will appear here once they register.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#fafafa' }}>
                  <TableCell sx={{ fontWeight: 700, color: '#000000', borderBottom: '2px solid #ff2852' }}>
                    User
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#000000', borderBottom: '2px solid #ff2852' }}>
                    Contact
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#000000', borderBottom: '2px solid #ff2852' }}>
                    Role
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#000000', borderBottom: '2px solid #ff2852' }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#000000', borderBottom: '2px solid #ff2852' }}>
                    Seller Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#000000', borderBottom: '2px solid #ff2852' }}>
                    Joined
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#000000', borderBottom: '2px solid #ff2852' }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow
                    key={user._id}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#fffef5',
                      },
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ bgcolor: '#ff2852', color: '#000000', fontWeight: 600 }}>
                          {user.name?.charAt(0)?.toUpperCase() || 'U'}
                        </Avatar>
                        <Box>
                          <Typography sx={{ color: '#000000', fontWeight: 600 }}>
                            {user.name || 'N/A'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#666666' }}>
                            ID: {user._id?.substring(0, 8)}...
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Email sx={{ fontSize: 16, color: '#666666' }} />
                          <Typography variant="body2" sx={{ color: '#666666' }}>
                            {user.email || 'N/A'}
                          </Typography>
                        </Box>
                        {user.phone && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <Phone sx={{ fontSize: 16, color: '#666666' }} />
                            <Typography variant="body2" sx={{ color: '#666666' }}>
                              {user.phone}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {Array.isArray(user.role) ? (
                          user.role.map((r, idx) => (
                            <Chip
                              key={idx}
                              label={r.charAt(0).toUpperCase() + r.slice(1)}
                              sx={{
                                backgroundColor: r === 'admin' ? '#ff2852' : r === 'seller' ? '#FF9800' : r === 'buyer' ? '#4CAF50' : '#f5f5f5',
                                color: ['admin', 'seller', 'buyer'].includes(r) ? '#ffffff' : '#666666',
                                fontWeight: 600,
                              }}
                              size="small"
                            />
                          ))
                        ) : (
                          <Chip
                            label={user.role ? (user.role.charAt(0).toUpperCase() + user.role.slice(1)) : 'Customer'}
                            sx={{
                              backgroundColor: user.role === 'admin' ? '#ff2852' : user.role === 'seller' ? '#FF9800' : '#f5f5f5',
                              color: user.role === 'admin' || user.role === 'seller' ? '#ffffff' : '#666666',
                              fontWeight: 600,
                            }}
                            size="small"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.isActive !== false ? 'Active' : 'Inactive'}
                        sx={{
                          backgroundColor: user.isActive !== false ? '#4caf50' : '#9e9e9e',
                          color: '#ffffff',
                          fontWeight: 600,
                        }}
                        size="small"
                      />
                    </TableCell>
                    {/* Seller Status Column */}
                    <TableCell>
                      {(Array.isArray(user.role) ? user.role.includes('seller') : user.role === 'seller') ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Chip
                            icon={
                              user.sellerStatus === 'verified' ? <VerifiedUser sx={{ fontSize: 16 }} /> :
                                user.sellerStatus === 'rejected' ? <Cancel sx={{ fontSize: 16 }} /> :
                                  <HourglassEmpty sx={{ fontSize: 16 }} />
                            }
                            label={
                              user.sellerStatus === 'verified' ? 'Verified' :
                                user.sellerStatus === 'rejected' ? 'Rejected' :
                                  'Pending'
                            }
                            sx={{
                              backgroundColor:
                                user.sellerStatus === 'verified' ? '#4CAF50' :
                                  user.sellerStatus === 'rejected' ? '#f44336' :
                                    '#FF9800',
                              color: '#ffffff',
                              fontWeight: 600,
                            }}
                            size="small"
                          />
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                            {user.sellerStatus !== 'verified' && (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleVerifySeller(user, 'verified')}
                                sx={{
                                  minWidth: 'auto',
                                  px: 1,
                                  py: 0.25,
                                  fontSize: '0.65rem',
                                  backgroundColor: '#4CAF50',
                                  '&:hover': { backgroundColor: '#388E3C' }
                                }}
                              >
                                Verify
                              </Button>
                            )}
                            {user.sellerStatus !== 'rejected' && (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleVerifySeller(user, 'rejected')}
                                sx={{
                                  minWidth: 'auto',
                                  px: 1,
                                  py: 0.25,
                                  fontSize: '0.65rem',
                                  backgroundColor: '#f44336',
                                  '&:hover': { backgroundColor: '#d32f2f' }
                                }}
                              >
                                Reject
                              </Button>
                            )}
                            {user.sellerStatus !== 'pending' && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleVerifySeller(user, 'pending')}
                                sx={{
                                  minWidth: 'auto',
                                  px: 1,
                                  py: 0.25,
                                  fontSize: '0.65rem',
                                  borderColor: '#FF9800',
                                  color: '#FF9800',
                                  '&:hover': { borderColor: '#F57C00', backgroundColor: 'rgba(255, 152, 0, 0.1)' }
                                }}
                              >
                                Pending
                              </Button>
                            )}
                          </Box>
                        </Box>
                      ) : (
                        <Chip
                          label="N/A"
                          size="small"
                          sx={{ backgroundColor: '#e0e0e0', color: '#666666' }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ color: '#666666' }}>
                      {user.createdAt
                        ? new Date(user.createdAt).toLocaleDateString()
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <IconButton
                          onClick={() => handleToggleStatus(user)}
                          sx={{
                            color: user.isActive !== false ? '#f44336' : '#4caf50',
                            '&:hover': {
                              backgroundColor: user.isActive !== false
                                ? 'rgba(244, 67, 54, 0.1)'
                                : 'rgba(76, 175, 80, 0.1)',
                            },
                          }}
                          size="small"
                        >
                          {user.isActive !== false ? <Block /> : <CheckCircle />}
                        </IconButton>
                        <IconButton
                          onClick={() => handleEdit(user)}
                          sx={{
                            color: '#ff2852',
                            '&:hover': {
                              backgroundColor: 'rgba(123, 44, 191, 0.1)',
                            },
                          }}
                          size="small"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDelete(user._id)}
                          sx={{
                            color: '#f44336',
                            '&:hover': {
                              backgroundColor: 'rgba(244, 67, 54, 0.1)',
                            },
                          }}
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* User Details Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          backgroundColor: '#FFFFFF',
          color: '#ff2852',
          fontWeight: 700,
          borderBottom: '2px solid #ff2852',
        }}>
          User Details
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedUser && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Avatar sx={{ width: 80, height: 80, bgcolor: '#ff2852', color: '#000000', fontSize: '2rem', fontWeight: 700 }}>
                    {selectedUser.name?.charAt(0)?.toUpperCase() || 'U'}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ color: '#000000', fontWeight: 700 }}>
                      {selectedUser.name || 'N/A'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666666' }}>
                      {selectedUser.email || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Email sx={{ color: '#ff2852' }} />
                  <Typography variant="body2" sx={{ color: '#666666' }}>
                    <strong>Email:</strong> {selectedUser.email || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Phone sx={{ color: '#ff2852' }} />
                  <Typography variant="body2" sx={{ color: '#666666' }}>
                    <strong>Phone:</strong> {selectedUser.phone || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Person sx={{ color: '#ff2852' }} />
                  <Typography variant="body2" sx={{ color: '#666666' }}>
                    <strong>Role:</strong> {selectedUser.role || 'Customer'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <CalendarToday sx={{ color: '#ff2852' }} />
                  <Typography variant="body2" sx={{ color: '#666666' }}>
                    <strong>Joined:</strong>{' '}
                    {selectedUser.createdAt
                      ? new Date(selectedUser.createdAt).toLocaleDateString()
                      : 'N/A'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: '#666666',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
